/**
 * AdminCatalogueEntryPage - Full-page production setup for a catalogue entry
 * Links catalogue titles to Sales Rate Card SKUs (sales_rate_items)
 * with BOM resolution via the sales item's bom_product_id
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { API_BASE } from '../lib/apiBase';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
    ArrowLeft, Tag, Star, X, Plus, Loader2, Link2, Search,
    ChevronRight, Package, Layers, Settings, Truck, Palette, Disc,
    Image as ImageIcon, ExternalLink, CheckCircle2
} from 'lucide-react';

const API = API_BASE + '/api';

// Map sales_rate_items categories to display groups
const CATEGORY_CONFIG = {
    'vinyl':        { group: 'Product',    icon: Disc,     label: 'Vinyl',        order: 0 },
    'product':      { group: 'Product',    icon: Package,  label: 'Product',      order: 1 },
    'mastering':    { group: 'Product',    icon: Settings, label: 'Mastering',    order: 2 },
    'label':        { group: 'Components', icon: Tag,      label: 'Labels',       order: 10 },
    'outersleeve':  { group: 'Components', icon: Layers,   label: 'Outer Sleeves', order: 11 },
    'innersleeve':  { group: 'Components', icon: Layers,   label: 'Inner Sleeves', order: 12 },
    'insert':       { group: 'Components', icon: Layers,   label: 'Inserts',      order: 13 },
    'sticker':      { group: 'Components', icon: Tag,      label: 'Stickers',     order: 14 },
    'assempling':   { group: 'Services',   icon: Settings, label: 'Assembling',   order: 20 },
};

function getCategoryInfo(cat) {
    return CATEGORY_CONFIG[cat] || { group: 'Other', icon: Tag, label: cat, order: 99 };
}

export default function AdminCatalogueEntryPage() {
    const { companyId, entryId } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [addingSkus, setAddingSkus] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchOpen, setSearchOpen] = useState(false);
    const searchRef = useRef(null);

    // Discogs cover lookup state
    const [discogsOpen, setDiscogsOpen] = useState(false);

    useEffect(() => {
        fetchEntry();
        fetchCategories();
    }, [companyId, entryId]); // eslint-disable-line

    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fetchEntry = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/organizations/${companyId}/catalogue/${entryId}/detail`, { headers });
            setEntry(res.data);
        } catch {
            toast.error('Failed to load catalogue entry');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${API}/admin/sales-rate-items/by-category`, { headers });
            setCategories(res.data || []);
        } catch {
            toast.error('Failed to load Sales Rate Card categories');
        }
    };

    const handleAddSku = async (item) => {
        const key = item.id || item.sku;
        setAddingSkus(prev => ({ ...prev, [key]: true }));
        try {
            const res = await axios.post(
                `${API}/admin/organizations/${companyId}/catalogue/${entryId}/sku-links`,
                { sku: item.sku, sales_item_id: item.id },
                { headers }
            );
            setEntry(prev => ({ ...prev, sku_links: res.data.sku_links, pricing_linked: true }));
            toast.success(`SKU ${item.sku} linked`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to link SKU');
        } finally {
            setAddingSkus(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleRemoveSku = async (link) => {
        const identifier = link.sales_item_id || link.sku;
        try {
            const res = await axios.delete(
                `${API}/admin/organizations/${companyId}/catalogue/${entryId}/sku-links/${encodeURIComponent(identifier)}`,
                { headers }
            );
            setEntry(prev => ({ ...prev, sku_links: res.data.sku_links, pricing_linked: res.data.sku_links.length > 0 }));
            toast.success(`SKU ${link.sku} unlinked`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to unlink SKU');
        }
    };

    const handleSetPrimary = async (link) => {
        const identifier = link.sales_item_id || link.sku;
        try {
            const res = await axios.put(
                `${API}/admin/organizations/${companyId}/catalogue/${entryId}/sku-links/${encodeURIComponent(identifier)}/set-primary`,
                {},
                { headers }
            );
            setEntry(prev => ({ ...prev, sku_links: res.data.sku_links }));
            toast.success('Primary SKU updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to set primary');
        }
    };

    const handleToggleLinkFlag = async (link, field, nextValue) => {
        // nextValue may be true / false / null (null clears the explicit
        // override and falls back to the category-aware smart default).
        const identifier = link.sales_item_id || link.sku;
        try {
            const res = await axios.patch(
                `${API}/admin/organizations/${companyId}/catalogue/${entryId}/sku-links/${encodeURIComponent(identifier)}`,
                { [field]: nextValue },
                { headers }
            );
            setEntry(prev => ({ ...prev, sku_links: res.data.sku_links }));
            toast.success('SKU link updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update SKU link');
        }
    };

    const searchSkus = async (q) => {
        setSearchQuery(q);
        if (q.length < 1) { setSearchResults([]); setSearchOpen(false); return; }
        try {
            const res = await axios.get(`${API}/admin/sales-rate-items/search?q=${encodeURIComponent(q)}`, { headers });
            const linkedIds = new Set((entry?.sku_links || []).map(l => l.sales_item_id));
            setSearchResults((res.data || []).filter(r => !linkedIds.has(r.id)));
            setSearchOpen(true);
        } catch {
            setSearchResults([]);
        }
    };

    const linkedIds = new Set((entry?.sku_links || []).map(l => l.sales_item_id));

    // Group linked SKUs by their category
    const groupedLinks = {};
    (entry?.sku_links || []).forEach(link => {
        const catInfo = getCategoryInfo(link.category || '');
        const group = catInfo.group;
        if (!groupedLinks[group]) groupedLinks[group] = [];
        groupedLinks[group].push(link);
    });

    const groupOrder = ['Product', 'Components', 'Services', 'Other'];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-spinner">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!entry) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <p className="text-slate-500">Entry not found</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6" data-testid="admin-catalogue-entry-page">
            {/* Header */}
            <button
                onClick={() => navigate(`/admin/companies/${companyId}?tab=catalogue`)}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
                data-testid="back-to-catalogue-library"
            >
                <ArrowLeft className="h-4 w-4" /> Back to Catalogue Library
            </button>

            <div className="flex items-start justify-between mb-8 gap-6">
                <div className="flex items-start gap-4">
                    {/* Cover thumbnail */}
                    <div className="flex-shrink-0">
                        {entry.cover_image ? (
                            <div className="relative group">
                                <img
                                    src={entry.cover_image}
                                    alt={entry.title || 'Cover'}
                                    className="w-24 h-24 rounded-lg object-cover border border-slate-200 shadow-sm"
                                    data-testid="catalogue-cover-image"
                                />
                                {entry.cover_image_source === 'discogs' && (
                                    <span className="absolute -bottom-2 -right-2 inline-flex items-center gap-1 bg-slate-900 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                                        Discogs
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className="w-24 h-24 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                                <ImageIcon className="h-6 w-6 mb-1" />
                                <span className="text-[10px]">No cover</span>
                            </div>
                        )}
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="mt-2 w-24 text-xs h-7"
                            onClick={() => setDiscogsOpen(true)}
                            data-testid="lookup-discogs-btn"
                        >
                            <Search className="h-3 w-3 mr-1" />
                            {entry.cover_image ? 'Change' : 'Find on Discogs'}
                        </Button>
                    </div>
                    <div>
                        <span className="font-mono text-sm text-slate-400 block">{entry.catalogue_number}</span>
                        <h1 className="text-2xl font-bold text-slate-900 mt-1">{entry.title || 'Untitled'}</h1>
                        {entry.artist && <p className="text-base text-slate-500 mt-0.5">{entry.artist}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-3 text-sm flex-wrap justify-end">
                    {entry.media_type && (
                        <span className={`px-2 py-1 rounded font-medium text-xs ${
                            {vinyl: 'bg-amber-50 text-amber-700', cd: 'bg-sky-50 text-sky-700', dvd: 'bg-violet-50 text-violet-700', blu_ray: 'bg-indigo-50 text-indigo-700'}[entry.media_type?.toLowerCase()] || 'bg-slate-100 text-slate-600'
                        }`}>
                            {{vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', blu_ray: 'Blu-ray'}[entry.media_type?.toLowerCase()] || entry.media_type}
                        </span>
                    )}
                    {entry.format && (
                        <span className="px-2 py-1 rounded bg-slate-100 font-medium">
                            {entry.format}{/^\d+$/.test(entry.format) ? '"' : ''}
                        </span>
                    )}
                    {entry.source === 'import' ? (
                        <span className="px-2 py-1 rounded bg-blue-50 text-blue-600 text-xs font-medium">Imported</span>
                    ) : (
                        <span className="px-2 py-1 rounded bg-slate-50 text-slate-500 text-xs font-medium">Owntune</span>
                    )}
                    {entry.pricing_linked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-xs font-medium">
                            <Link2 className="h-3 w-3" /> Sales SKU linked
                        </span>
                    ) : (
                        <span className="px-2 py-1 rounded bg-amber-50 text-amber-600 text-xs font-medium">No Sales SKU</span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Left: Category-based SKU selection from Sales Rate Cards */}
                <div className="col-span-5">
                    <Card className="border-slate-200 sticky top-6">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold text-slate-700">Add SKU from Sales Rate Cards</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {/* Quick search */}
                            <div className="relative" ref={searchRef}>
                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                                <Input
                                    value={searchQuery}
                                    onChange={(e) => searchSkus(e.target.value)}
                                    onFocus={() => { if (searchQuery.length >= 1 && searchResults.length > 0) setSearchOpen(true); }}
                                    placeholder="Quick search Sales SKU..."
                                    className="text-sm h-9 pl-8"
                                    data-testid="sku-search-input"
                                />
                                {searchOpen && searchResults.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {searchResults.map(item => (
                                            <button key={item.id} type="button" className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0 text-sm"
                                                onClick={() => { handleAddSku(item); setSearchQuery(''); setSearchOpen(false); }}
                                                data-testid={`search-result-${item.sku}`}>
                                                <span className="font-mono font-medium text-slate-800">{item.sku}</span>
                                                <span className="text-xs text-slate-500 ml-2">{item.name}</span>
                                                {item.rate_card_name && <span className="text-[10px] text-slate-400 float-right mt-0.5">{item.rate_card_name}</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <hr className="border-slate-100" />

                            {/* Category list or SKU list */}
                            {!selectedCategory ? (
                                <div className="space-y-1" data-testid="category-list">
                                    {categories.map(cat => {
                                        const info = getCategoryInfo(cat.category);
                                        const Icon = info.icon;
                                        return (
                                            <button
                                                key={cat.category}
                                                onClick={() => setSelectedCategory(cat)}
                                                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                                                data-testid={`category-${cat.category}`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <Icon className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
                                                    <div>
                                                        <span className="text-sm font-medium text-slate-700">{info.label}</span>
                                                        <span className="text-xs text-slate-400 ml-2">{cat.count}</span>
                                                    </div>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" />
                                            </button>
                                        );
                                    })}
                                    {categories.length === 0 && (
                                        <p className="text-sm text-slate-400 text-center py-6">No Sales Rate Card items found</p>
                                    )}
                                </div>
                            ) : (
                                <div data-testid="sku-list">
                                    <button
                                        onClick={() => setSelectedCategory(null)}
                                        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3 transition-colors"
                                        data-testid="back-to-categories"
                                    >
                                        <ArrowLeft className="h-3 w-3" /> All categories
                                    </button>
                                    <h3 className="text-sm font-semibold text-slate-700 mb-2">{getCategoryInfo(selectedCategory.category).label}</h3>
                                    <div className="space-y-0.5 max-h-[50vh] overflow-y-auto">
                                        {selectedCategory.items.map(item => {
                                            const isLinked = linkedIds.has(item.id);
                                            return (
                                                <div
                                                    key={item.id}
                                                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${isLinked ? 'bg-emerald-50/50' : 'hover:bg-slate-50'}`}
                                                    data-testid={`sku-item-${item.sku}`}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <span className="font-mono text-xs font-medium text-slate-800">{item.sku}</span>
                                                        <p className="text-xs text-slate-500 truncate">{item.name}</p>
                                                        {item.rate_card_name && <p className="text-[10px] text-slate-400">{item.rate_card_name}</p>}
                                                    </div>
                                                    {isLinked ? (
                                                        <span className="text-[10px] text-emerald-600 font-medium shrink-0 ml-2">Linked</span>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs shrink-0 ml-2"
                                                            onClick={() => handleAddSku(item)}
                                                            disabled={addingSkus[item.id]}
                                                            data-testid={`add-sku-${item.sku}`}
                                                        >
                                                            {addingSkus[item.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="h-3 w-3 mr-0.5" /> Add</>}
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Right: Linked SKUs grouped by type */}
                <div className="col-span-7 space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-semibold text-slate-800">Production Setup</h2>
                        <span className="text-xs text-slate-400">{(entry.sku_links || []).length} SKU{(entry.sku_links || []).length !== 1 ? 's' : ''} linked</span>
                    </div>

                    {(entry.sku_links || []).length === 0 ? (
                        <Card className="border-dashed border-slate-200">
                            <CardContent className="py-12 text-center">
                                <Tag className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-sm text-slate-500 font-medium">No Sales SKUs linked</p>
                                <p className="text-xs text-slate-400 mt-1">Select a category on the left to start adding SKUs from Sales Rate Cards</p>
                            </CardContent>
                        </Card>
                    ) : (
                        groupOrder.filter(g => groupedLinks[g]?.length > 0).map(group => (
                            <div key={group} data-testid={`group-${group.toLowerCase()}`}>
                                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{group}</h3>
                                <div className="space-y-3">
                                    {groupedLinks[group].map(link => (
                                        <Card key={link.sales_item_id || link.sku} className={`border ${link.is_primary ? 'border-indigo-200 bg-indigo-50/10' : 'border-slate-200'}`} data-testid={`linked-sku-${link.sku}`}>
                                            <CardContent className="p-4">
                                                {/* Header */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-mono text-sm font-bold text-slate-800">{link.sku}</span>
                                                        {link.is_primary && (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                                                <Star className="h-2.5 w-2.5 fill-current" /> Primary
                                                            </span>
                                                        )}
                                                        {link.is_active === false ? (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600">Inactive</span>
                                                        ) : (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600">Active</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {!link.is_primary && (
                                                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500 hover:text-indigo-600"
                                                                onClick={() => handleSetPrimary(link)}
                                                                data-testid={`set-primary-${link.sku}`}>
                                                                <Star className="h-3 w-3 mr-1" /> Set primary
                                                            </Button>
                                                        )}
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                                                            onClick={() => handleRemoveSku(link)}
                                                            data-testid={`remove-sku-${link.sku}`}>
                                                            <X className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* Info grid */}
                                                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs mb-3">
                                                    <div>
                                                        <span className="text-[10px] text-slate-400 uppercase tracking-wide">Display Name</span>
                                                        <p className="text-slate-700 font-medium">{link.display_name || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-[10px] text-slate-400 uppercase tracking-wide">UOM</span>
                                                        <p className="text-slate-600">{link.uom || '-'}</p>
                                                    </div>
                                                    {link.rate_card_name && (
                                                        <div>
                                                            <span className="text-[10px] text-slate-400 uppercase tracking-wide">Rate Card</span>
                                                            <p className="text-slate-600">{link.rate_card_name}</p>
                                                        </div>
                                                    )}
                                                    {link.bom_product_name && (
                                                        <div>
                                                            <span className="text-[10px] text-slate-400 uppercase tracking-wide">BOM Product</span>
                                                            <p className="text-amber-700 font-medium">{link.bom_product_name}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* BOM References */}
                                                {link.bom_references?.length > 0 && (
                                                    <div className="bg-amber-50/60 border border-amber-100 rounded p-2.5 mb-3">
                                                        <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">BOM</p>
                                                        {link.bom_references.map((bom, idx) => (
                                                            <div key={idx} className="flex items-center justify-between text-xs">
                                                                <span className="text-amber-800 font-medium">{bom.bom_name}</span>
                                                                {bom.bom_category && <span className="text-amber-600 text-[10px]">{bom.bom_category}</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Reorder pricing controls */}
                                                {link.link_flags && (
                                                    <div className="bg-slate-50 border border-slate-100 rounded p-2.5 mb-3" data-testid={`link-flags-${link.sku}`}>
                                                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Reorder pricing</p>
                                                        <div className="space-y-1">
                                                            <LinkFlagToggle
                                                                label="Active in pricing"
                                                                hint="Include this SKU when calculating order/reorder price"
                                                                effective={link.link_flags.is_active}
                                                                explicit={link.explicit_is_active}
                                                                onChange={(v) => handleToggleLinkFlag(link, 'is_active', v)}
                                                                testid={`flag-active-${link.sku}`}
                                                            />
                                                            <LinkFlagToggle
                                                                label="Apply to reorder"
                                                                hint={
                                                                    link.link_flags.is_setup_like_default
                                                                        ? 'Category looks like a setup/one-off cost — excluded from reorder by default'
                                                                        : 'Include this SKU when pricing a reorder'
                                                                }
                                                                effective={link.link_flags.applies_to_reorder}
                                                                explicit={link.explicit_applies_to_reorder}
                                                                onChange={(v) => handleToggleLinkFlag(link, 'applies_to_reorder', v)}
                                                                testid={`flag-reorder-${link.sku}`}
                                                            />
                                                            <LinkFlagToggle
                                                                label="Scales with quantity"
                                                                hint="ON = unit_price × order qty. OFF = one-off charge (e.g. test press)"
                                                                effective={link.link_flags.qty_scales_with_order}
                                                                explicit={link.explicit_qty_scales_with_order}
                                                                onChange={(v) => handleToggleLinkFlag(link, 'qty_scales_with_order', v)}
                                                                testid={`flag-scales-${link.sku}`}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Sales Price Tiers */}
                                                {link.price_tiers?.length > 0 && (
                                                    <div className="bg-slate-50 border border-slate-100 rounded p-2.5">
                                                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Sales Price Tiers</p>
                                                        <table className="w-full text-xs">
                                                            <thead>
                                                                <tr className="text-[10px] text-slate-400 uppercase">
                                                                    <th className="text-left py-0.5 font-medium">Min Qty</th>
                                                                    <th className="text-right py-0.5 font-medium">Unit Price</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {link.price_tiers.slice(0, 5).map((tier, idx) => (
                                                                    <tr key={idx} className="border-t border-slate-100/80">
                                                                        <td className="py-1 text-slate-600">{tier.min_qty}+</td>
                                                                        <td className="py-1 text-right text-emerald-600 font-medium">{tier.unit_price != null ? Number(tier.unit_price).toFixed(2) : '-'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {link.price_tiers.length > 5 && (
                                                            <p className="text-[10px] text-slate-400 mt-1 text-center">+{link.price_tiers.length - 5} more</p>
                                                        )}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Discogs cover lookup dialog */}
            <DiscogsCoverDialog
                open={discogsOpen}
                onClose={() => setDiscogsOpen(false)}
                entry={entry}
                companyId={companyId}
                onApplied={(payload) => {
                    setEntry(prev => ({
                        ...prev,
                        cover_image: payload.cover_image,
                        cover_image_source: payload.cover_image_source,
                        cover_image_discogs_release_id: payload.cover_image_discogs_release_id,
                    }));
                    setDiscogsOpen(false);
                    fetchEntry();
                }}
            />
        </div>
    );
}

// ==================== Discogs Cover Lookup Dialog ====================

function DiscogsCoverDialog({ open, onClose, entry, companyId, onApplied }) {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const [catno, setCatno] = useState('');
    const [labelFilter, setLabelFilter] = useState('');
    const [artistQuery, setArtistQuery] = useState('');
    const [titleQuery, setTitleQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [searched, setSearched] = useState(false);

    const [selectedRelease, setSelectedRelease] = useState(null); // {id, title, ...}
    const [releaseImages, setReleaseImages] = useState([]);
    const [loadingRelease, setLoadingRelease] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [applying, setApplying] = useState(false);
    const [confirmReplace, setConfirmReplace] = useState(false);

    // Reset when opened with a fresh entry. Prefill artist / title /
    // catalogue number from the catalogue entry so admins land in a
    // sensible starting state — but every field is editable and any
    // combination is a valid search (including releases without a
    // catalogue number on Discogs).
    useEffect(() => {
        if (!open) return;
        setCatno(entry?.catalogue_number || '');
        setArtistQuery(entry?.artist || '');
        setTitleQuery(entry?.title || '');
        setLabelFilter('');
        setResults([]);
        setSearched(false);
        setSelectedRelease(null);
        setReleaseImages([]);
        setSelectedImage(null);
        setConfirmReplace(false);
    }, [open, entry?.catalogue_number, entry?.artist, entry?.title]);

    const runSearch = async () => {
        // Any single field is enough — Discogs releases with "none"
        // as catalogue number are perfectly valid, so we deliberately
        // allow artist-only / title-only / label-only searches too.
        if (!catno && !labelFilter && !artistQuery && !titleQuery) {
            toast.error('Enter at least one of catalogue number, artist, title, or label');
            return;
        }
        setSearching(true);
        setSearched(false);
        setSelectedRelease(null);
        setReleaseImages([]);
        try {
            const params = {};
            if (catno) params.catalogue_number = catno;
            if (labelFilter) params.label = labelFilter;
            if (artistQuery) params.artist = artistQuery;
            if (titleQuery) params.title = titleQuery;
            const res = await axios.get(`${API}/discogs/search`, { headers, params });
            setResults(res.data?.results || []);
            setSearched(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Discogs search failed');
        } finally {
            setSearching(false);
        }
    };

    const pickRelease = async (release) => {
        setSelectedRelease(release);
        setReleaseImages([]);
        setSelectedImage(null);
        setLoadingRelease(true);
        try {
            const res = await axios.get(`${API}/discogs/releases/${release.id}`, { headers });
            const imgs = res.data?.images || [];
            setReleaseImages(imgs);
            // Auto-select primary if only one
            if (imgs.length > 0) setSelectedImage(imgs[0]);

            // Merge the cleaner release-detail title + separated artists_sort
            // into selectedRelease. Discogs search results return `title` in
            // the combined format "Artist - Title" while the release-detail
            // endpoint exposes the bare title and `artists_sort` separately —
            // we need both so applyCover can send them as discogs_title /
            // discogs_artist instead of dumping "Artist - Title" into Title.
            const cleanTitle = (res.data?.title || release.title || '').trim();
            const cleanArtist = (res.data?.artists_sort || '').trim();
            setSelectedRelease((prev) => ({
                ...(prev || release),
                title: cleanTitle || prev?.title || release.title,
                artists_sort: cleanArtist || prev?.artists_sort || null,
            }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load release');
        } finally {
            setLoadingRelease(false);
        }
    };

    const applyCover = async (force = false) => {
        if (!selectedImage || !selectedRelease) return;
        setApplying(true);
        try {
            const res = await axios.post(
                `${API}/discogs/admin/companies/${companyId}/catalogue/${entry.id}/cover-from-discogs`,
                {
                    release_id: selectedRelease.id,
                    image_url: selectedImage.uri,
                    force,
                    // Send Discogs metadata so backend can fill empty title/artist
                    discogs_title: selectedRelease.title || null,
                    discogs_artist: selectedRelease.artists_sort || null,
                },
                { headers }
            );
            const filled = res.data?.filled_fields || [];
            if (filled.length) {
                toast.success(`Cover applied · filled ${filled.join(' & ')} from Discogs`);
            } else {
                toast.success('Cover applied');
            }
            onApplied?.(res.data);
        } catch (err) {
            if (err.response?.status === 409 && !force) {
                setConfirmReplace(true);
            } else {
                toast.error(err.response?.data?.detail || 'Failed to apply cover');
            }
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="discogs-cover-dialog">
                <DialogHeader>
                    <DialogTitle>Find cover on Discogs</DialogTitle>
                    <DialogDescription>
                        Manual lookup. We will fetch the cover image and only fill missing
                        title/artist fields. Existing metadata is never overwritten.
                    </DialogDescription>
                </DialogHeader>

                {/* Search form — prefilled from the catalogue entry.
                    Any combination of fields works; Discogs releases
                    with catno "none" are matched by artist/title alone. */}
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium text-slate-600 mb-1 block">Artist</label>
                            <Input
                                value={artistQuery}
                                onChange={(e) => setArtistQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                                placeholder="e.g. Jakob Sveistrup"
                                data-testid="discogs-artist-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-600 mb-1 block">Title</label>
                            <Input
                                value={titleQuery}
                                onChange={(e) => setTitleQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                                placeholder="e.g. In Your Face"
                                data-testid="discogs-title-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-600 mb-1 block">Catalogue Number</label>
                            <Input
                                value={catno}
                                onChange={(e) => setCatno(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                                placeholder="e.g. NB-2024-001"
                                data-testid="discogs-catno-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-600 mb-1 block">Label</label>
                            <Input
                                value={labelFilter}
                                onChange={(e) => setLabelFilter(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                                placeholder="e.g. Sony Music"
                                data-testid="discogs-label-input"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={runSearch} disabled={searching} data-testid="discogs-search-btn">
                            {searching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                            Search Discogs
                        </Button>
                        {searched && results.length > 1 && !selectedRelease && (
                            <span className="text-xs text-amber-700 self-center">
                                {results.length} matches — pick the right one below
                            </span>
                        )}
                    </div>
                </div>

                {/* Results */}
                {!selectedRelease && searched && (
                    <div className="border-t border-slate-200 pt-4 space-y-2 max-h-80 overflow-y-auto" data-testid="discogs-results">
                        {results.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-6">No releases found.</p>
                        ) : (
                            results.map((r) => {
                                // Discogs search results pack artist +
                                // title together as "Artist - Title".
                                // Split on the first " - " so we can
                                // render each field on its own row —
                                // falls back to the raw title when the
                                // delimiter isn't present.
                                const dashIdx = (r.title || '').indexOf(' - ');
                                const rowArtist = dashIdx > 0 ? r.title.slice(0, dashIdx) : '';
                                const rowTitle = dashIdx > 0 ? r.title.slice(dashIdx + 3) : (r.title || '');
                                return (
                                    <button
                                        key={r.id}
                                        type="button"
                                        onClick={() => pickRelease(r)}
                                        className="w-full text-left p-2 rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors flex items-center gap-3"
                                        data-testid={`discogs-result-${r.id}`}
                                    >
                                        {r.thumb ? (
                                            <img src={r.thumb} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
                                        ) : (
                                            <div className="w-14 h-14 rounded bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0">
                                                <ImageIcon className="h-5 w-5" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            {rowArtist && (
                                                <p className="text-xs text-slate-500 truncate">{rowArtist}</p>
                                            )}
                                            <p className="text-sm font-medium text-slate-900 truncate">{rowTitle}</p>
                                            <p className="text-[11px] text-slate-500 truncate">
                                                {[
                                                    r.format,
                                                    r.year,
                                                    r.label,
                                                    r.catno ? `Cat# ${r.catno}` : null,
                                                ].filter(Boolean).join(' · ')}
                                            </p>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                                    </button>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Release detail / image picker */}
                {selectedRelease && (
                    <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="discogs-image-picker">
                        <div className="flex items-center justify-between">
                            <div className="text-sm">
                                <button
                                    type="button"
                                    onClick={() => { setSelectedRelease(null); setReleaseImages([]); setSelectedImage(null); }}
                                    className="text-slate-500 hover:text-slate-900 text-xs underline"
                                >
                                    ← back to results
                                </button>
                                <p className="font-medium mt-1">{selectedRelease.title}</p>
                                <p className="text-xs text-slate-500">
                                    {[selectedRelease.catno, selectedRelease.label, selectedRelease.year].filter(Boolean).join(' • ')}
                                </p>
                            </div>
                            {selectedRelease.uri && (
                                <a
                                    href={selectedRelease.uri.startsWith('http') ? selectedRelease.uri : `https://www.discogs.com${selectedRelease.uri}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                                >
                                    <ExternalLink className="h-3 w-3" /> View on Discogs
                                </a>
                            )}
                        </div>

                        {loadingRelease ? (
                            <div className="text-center py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                        ) : releaseImages.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-6">This release has no images on Discogs.</p>
                        ) : (
                            <div className="grid grid-cols-3 gap-3">
                                {releaseImages.map((img, idx) => {
                                    const isSelected = selectedImage?.uri === img.uri;
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => setSelectedImage(img)}
                                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                                isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-slate-400'
                                            }`}
                                            data-testid={`discogs-image-${idx}`}
                                        >
                                            <img src={img.uri150 || img.uri} alt="" className="w-full h-full object-cover" />
                                            {img.type && (
                                                <span className="absolute top-1 left-1 text-[10px] bg-slate-900/80 text-white px-1.5 py-0.5 rounded">
                                                    {img.type}
                                                </span>
                                            )}
                                            {isSelected && (
                                                <div className="absolute inset-0 bg-emerald-500/10 flex items-center justify-center">
                                                    <CheckCircle2 className="h-8 w-8 text-emerald-600 drop-shadow-md" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Replace confirmation banner */}
                {confirmReplace && (
                    <div className="border-t border-amber-200 bg-amber-50 -mx-6 px-6 py-3 mt-4 text-sm text-amber-800">
                        <p className="font-medium mb-1">Replace existing cover?</p>
                        <p className="text-xs">This will overwrite the current cover image. This cannot be undone.</p>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    {confirmReplace ? (
                        <Button
                            onClick={() => { setConfirmReplace(false); applyCover(true); }}
                            disabled={applying}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="discogs-confirm-replace-btn"
                        >
                            {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Yes, Replace Cover
                        </Button>
                    ) : (
                        <Button
                            onClick={() => applyCover(false)}
                            disabled={!selectedImage || applying}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="discogs-apply-btn"
                        >
                            {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                            Use this cover
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Per-link tri-state pricing flag toggle.
 *
 * Each flag has THREE persisted states:
 *   - explicit `true`  → override default to ON
 *   - explicit `false` → override default to OFF
 *   - explicit `null`  → no override, use category-aware smart default
 *
 * UI: click the pill to cycle through Default → On → Off → Default → …
 * Effective state is shown next to the label so admin sees what's actually
 * being applied to pricing.
 */
function LinkFlagToggle({ label, hint, effective, explicit, onChange, testid }) {
    // Cycle: null (default) → true → false → null
    const cycle = () => {
        if (explicit === null || explicit === undefined) onChange(true);
        else if (explicit === true) onChange(false);
        else onChange(null);
    };
    const isDefault = explicit === null || explicit === undefined;
    const pillClass = isDefault
        ? (effective
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-500 border-slate-200')
        : (explicit
            ? 'bg-emerald-600 text-white border-emerald-600'
            : 'bg-rose-600 text-white border-rose-600');
    const stateLabel = isDefault
        ? (effective ? 'Default: ON' : 'Default: OFF')
        : (explicit ? 'ON' : 'OFF');
    return (
        <div className="flex items-start justify-between gap-3 text-xs">
            <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-700">{label}</p>
                {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
            </div>
            <button
                type="button"
                onClick={cycle}
                className={`shrink-0 px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide transition ${pillClass}`}
                title={isDefault ? 'Click to override' : 'Click to cycle (On → Off → Default)'}
                data-testid={testid}
            >
                {stateLabel}
            </button>
        </div>
    );
}

